import { Col, Row } from '@advanza/grid'
import { SelectSimple } from '@advanza/input'
import { Icon } from '@advanza/ui'
import InputField from '@trustoo/components/input/InputField'
import {
    AnonymizedRequestsFilterOptions,
    changeAnonymizedRequestsFilter,
} from 'actions/anonymizedRequests'
import DatePicker from 'components/DatePicker'
import Pagination from 'components/Pagination'
import ServicesSelect from 'components/services/ServicesSelect'
import { localToUTC, SQL_DATE_TIME_FORMAT_FNS, utcToLocal } from 'date'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { ChangeEvent, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface AnonymizedRequestFilterProps {
    filterId?: string
    defaultOptions?: AnonymizedRequestsFilterOptions
}

const SERVICE_IDS_PARAM = 'serviceIds[]'
const QUERY_PARAM = 'query'
const START_DATE_PARAM = 'startDate'
const END_DATE_PARAM = 'endDate'

function getQueryFromSearchParams(searchParams: URLSearchParams) {
    const options: AnonymizedRequestsFilterOptions = {}

    const page = Number(searchParams.get('page'))
    if (page > 0) {
        options.page = page
    }

    const pageSize = Number(searchParams.get('pageSize'))
    if (pageSize) {
        options.pageSize = pageSize
    }

    const serviceIds = searchParams.getAll(SERVICE_IDS_PARAM)
    if (serviceIds && serviceIds.length > 0) {
        options.serviceIds = serviceIds.map((id) => Number(id))
    }

    const query = searchParams.get(QUERY_PARAM)
    if (query) {
        options.query = query
    }

    const startDate = searchParams.get(START_DATE_PARAM)
    if (startDate) {
        options.startDate = startDate
    }

    const endDate = searchParams.get(END_DATE_PARAM)
    if (endDate) {
        options.endDate = endDate
    }

    return options
}

function applyFilterOptionsToSearchParams(
    searchParams: URLSearchParams,
    options: AnonymizedRequestsFilterOptions,
) {
    const next = new URLSearchParams(searchParams)

    if ('page' in options) {
        if (options.page && options.page > 0) {
            next.set('page', options.page.toString())
        } else {
            next.delete('page')
        }
    }

    if ('pageSize' in options) {
        if (options.pageSize) {
            next.set('pageSize', options.pageSize.toString())
        } else {
            next.delete('pageSize')
        }
    }

    if ('serviceIds' in options) {
        next.delete(SERVICE_IDS_PARAM)
        if (options.serviceIds?.length) {
            options.serviceIds.forEach((id) => next.append(SERVICE_IDS_PARAM, id.toString()))
        }
    }

    if ('query' in options) {
        if (options.query) {
            next.set(QUERY_PARAM, options.query)
        } else {
            next.delete(QUERY_PARAM)
        }
    }

    if ('startDate' in options) {
        if (options.startDate) {
            next.set(START_DATE_PARAM, options.startDate)
        } else {
            next.delete(START_DATE_PARAM)
        }
    }

    if ('endDate' in options) {
        if (options.endDate) {
            next.set(END_DATE_PARAM, options.endDate)
        } else {
            next.delete(END_DATE_PARAM)
        }
    }

    return next
}

const AnonymizedRequestFilter = ({
    filterId = 'all',
    defaultOptions = {},
}: AnonymizedRequestFilterProps) => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const [query, setQuery] = useState('')
    const { filters, result, counts } = useAppSelector((state) => state.anonymizedRequests)
    const filter = filters[filterId]
    const filterResult = (filter && result[filter.searchKey]) || {}
    const count = filter && counts[filter.searchKey]
    const countOnPage = filter && (filterResult[filter.page || 0] || []).length

    function changeFilterWithUrl(options: AnonymizedRequestsFilterOptions) {
        setSearchParams((prev) => applyFilterOptionsToSearchParams(prev, options))
        dispatch(changeAnonymizedRequestsFilter(filterId, options))
    }

    useEffect(() => {
        changeFilterWithUrl({ ...defaultOptions, ...getQueryFromSearchParams(searchParams) })
    }, [])

    if (!filter) {
        return null
    }

    function onServiceIdsChange(serviceIds: number[]) {
        changeFilterWithUrl({ serviceIds, page: 0 })
    }

    function updateQuery(nextQuery: string) {
        changeFilterWithUrl({ query: nextQuery, page: 0 })
    }

    function onPageChange(page: number, invalidate = false) {
        changeFilterWithUrl({ page, didInvalidate: invalidate })
    }

    function onCountList() {
        changeFilterWithUrl({ countList: true, didInvalidate: true })
    }

    function invalidate() {
        changeFilterWithUrl({ didInvalidate: true, page: 0 })
    }

    function onDateChange(event: ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target
        if (value) {
            const dateTimeLocalStr =
                value.split(' ')[0] + ' ' + (name === 'startDate' ? '00:00:00' : '23:59:59')
            changeFilterWithUrl({
                [name]: localToUTC(dateTimeLocalStr, SQL_DATE_TIME_FORMAT_FNS),
                page: 0,
            })
        } else {
            changeFilterWithUrl({
                [name]: null,
                page: 0,
            })
        }
    }

    return (
        <Row middle="xs">
            <Col x>
                <ServicesSelect
                    useSelectedCountry={false}
                    large
                    onChangeValue={onServiceIdsChange}
                    value={filter.serviceIds || []}
                />
            </Col>
            <Col x>
                <InputField
                    title="Search by subject or location"
                    placeholder="search"
                    value={query || filter.query || ''}
                    onChange={(event) => {
                        const nextQuery = event.target.value
                        setQuery(nextQuery)
                        updateQuery(nextQuery)
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            updateQuery(query)
                        }
                    }}
                    before={<Icon name="search" grey />}
                />
            </Col>
            <Col x>
                <DatePicker
                    onChange={onDateChange}
                    dayPickerProps={{
                        disabled: (date) => date > new Date(),
                        showOutsideDays: false,
                    }}
                    value={utcToLocal(filter.startDate, SQL_DATE_TIME_FORMAT_FNS)}
                    name="startDate"
                />
            </Col>
            <Col x>
                <DatePicker
                    onChange={onDateChange}
                    dayPickerProps={{ disabled: (date) => date > new Date() }}
                    value={utcToLocal(filter.endDate, SQL_DATE_TIME_FORMAT_FNS)}
                    name="endDate"
                />
            </Col>
            <Col x>
                <SelectSimple
                    alwaysControlValue
                    value={filter.pageSize}
                    onChange={(e) => {
                        changeFilterWithUrl({
                            pageSize: Number(e.target.value),
                            page: 0,
                            pageSize_: Number(e.target.value),
                        })
                    }}
                    placeholder="page size"
                    options={[
                        { name: '20', value: 20 },
                        { name: '50', value: 50 },
                        { name: '100', value: 100 },
                        { name: '200', value: 200 },
                    ]}
                />
            </Col>
            <Col x>
                <button onClick={invalidate}>
                    <Icon name="refresh" />
                </button>
            </Col>
            <Col x style={{ marginLeft: 'auto' }}>
                <Pagination
                    current={filter.page || 0}
                    pageSize={filter.pageSize}
                    count={count}
                    countOnPage={countOnPage}
                    onCountList={onCountList}
                    onPageChange={onPageChange}
                />
            </Col>
        </Row>
    )
}

export default AnonymizedRequestFilter
